import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

// Contexts
import { AuthProvider, ProtectedRoute, AuthPromptProvider, useAuth, useAuthPrompt } from "@/context/AuthContext";
import { NotificationProvider, PushPromptBanner, useNotifications, NotifBadge } from "@/context/NotificationContext";

// Constants
import { CATEGORIES, CURRENCIES, LOCATIONS, SOCIAL_PLATFORMS, API, BACKEND_URL, GOOGLE_CLIENT_ID } from "@/lib/constants";

// Shared components (re-exported for backward compatibility)
import { MediaDisplay } from "@/components/shared/MediaDisplay";
import { FeatureCard } from "@/components/shared/FeatureCard";
import { SocialIcon, FollowUsLinks } from "@/components/shared/SocialIcon";
import { TopicCard } from "@/components/shared/TopicCard";
import { CategoryFilterBar } from "@/components/shared/CategoryFilterBar";
import { TypeFilterTabs } from "@/components/shared/TypeFilterTabs";
import { NewerPostsPill } from "@/components/shared/NewerPostsPill";
import { RichTextEditor } from "@/components/shared/RichTextEditor";

// Layout
import { MainLayout } from "@/components/layout/MainLayout";

// Re-export everything for backward compatibility with existing page imports from "@/App"
export { useAuth, useAuthPrompt, useNotifications };
export { CATEGORIES, CURRENCIES, LOCATIONS, SOCIAL_PLATFORMS, API, BACKEND_URL };
export { MediaDisplay, FeatureCard, SocialIcon, FollowUsLinks, TopicCard };
export { CategoryFilterBar, TypeFilterTabs, NewerPostsPill, RichTextEditor, NotifBadge };

// Page loader
const PageLoader = () => (
  <div className="min-h-screen bg-jeekota-bg flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-jeekota-primary" />
  </div>
);

// Lazy-loaded pages
const LazyLoginPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.LoginPage })));
const LazyVerifyEmailPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.VerifyEmailPage })));
const LazyForgotPasswordPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.ForgotPasswordPage })));
const LazyResetPasswordPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.ResetPasswordPage })));
const LazyNotificationsPage = lazy(() => import("@/pages/UserPages").then(m => ({ default: m.NotificationsPage })));
const LazyProfilePage = lazy(() => import("@/pages/UserPages").then(m => ({ default: m.ProfilePage })));
const LazyUserProfilePage = lazy(() => import("@/pages/UserPages").then(m => ({ default: m.UserProfilePage })));
const LazyTopicDetailPage = lazy(() => import("@/pages/TopicDetail").then(m => ({ default: m.TopicDetailPage })));
const LazyAdminPage = lazy(() => import("@/pages/AdminPages").then(m => ({ default: m.AdminPage })));
const LazyLegalPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.LegalPage })));
const LazyVisionMissionPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.VisionMissionPage })));
const LazyFeesPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.FeesPage })));
const LazyFAQPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.FAQPage })));
const LazyPrivacyPolicyPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage })));
const LazyTermsOfServicePage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.TermsOfServicePage })));
const LazyCommunityGuidelinesPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CommunityGuidelinesPage })));
const LazyAboutPage = lazy(() => import("@/pages/AboutPage").then(m => ({ default: m.AboutUsPage })));
const LazyInterestsPage = lazy(() => import("@/pages/InterestsPage").then(m => ({ default: m.InterestsPage })));
const LazyCreatorDashboard = lazy(() => import("@/pages/CreatorDashboard").then(m => ({ default: m.CreatorDashboardPage })));
const LazyContactPage = lazy(() => import("@/pages/ContactPage").then(m => ({ default: m.ContactUsPage })));
const LazyPaymentCallbackPage = lazy(() => import("@/pages/PaymentPage").then(m => ({ default: m.PaymentCallbackPage })));
const LazyHomePage = lazy(() => import("@/pages/HomePage"));
const LazyDiscussionsPage = lazy(() => import("@/pages/DiscussionsPage"));
const LazyCrowdfundingPage = lazy(() => import("@/pages/CrowdfundingPage"));
const LazyDonationsPage = lazy(() => import("@/pages/DonationsPage"));

// Router
const AppRouter = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<MainLayout><LazyHomePage /></MainLayout>} />
      <Route path="/home" element={<MainLayout><LazyHomePage /></MainLayout>} />
      <Route path="/discussions" element={<MainLayout><LazyDiscussionsPage /></MainLayout>} />
      <Route path="/crowdfunding" element={<MainLayout><LazyCrowdfundingPage /></MainLayout>} />
      <Route path="/donations" element={<MainLayout><LazyDonationsPage /></MainLayout>} />
      <Route path="/login" element={<LazyLoginPage />} />
      <Route path="/verify-email" element={<LazyVerifyEmailPage />} />
      <Route path="/forgot-password" element={<LazyForgotPasswordPage />} />
      <Route path="/reset-password" element={<LazyResetPasswordPage />} />
      <Route path="/topic/:id" element={<MainLayout><LazyTopicDetailPage /></MainLayout>} />
      <Route path="/user/:username" element={<MainLayout><LazyUserProfilePage /></MainLayout>} />
      <Route path="/notifications" element={<ProtectedRoute><MainLayout><LazyNotificationsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><MainLayout><LazyProfilePage /></MainLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><MainLayout><LazyAdminPage /></MainLayout></ProtectedRoute>} />
      <Route path="/legal" element={<MainLayout><LazyLegalPage /></MainLayout>} />
      <Route path="/privacy" element={<MainLayout><LazyPrivacyPolicyPage /></MainLayout>} />
      <Route path="/terms" element={<MainLayout><LazyTermsOfServicePage /></MainLayout>} />
      <Route path="/community-guidelines" element={<MainLayout><LazyCommunityGuidelinesPage /></MainLayout>} />
      <Route path="/vision" element={<MainLayout><LazyVisionMissionPage /></MainLayout>} />
      <Route path="/fees" element={<MainLayout><LazyFeesPage /></MainLayout>} />
      <Route path="/faq" element={<MainLayout><LazyFAQPage /></MainLayout>} />
      <Route path="/about" element={<MainLayout><LazyAboutPage /></MainLayout>} />
      <Route path="/interests" element={<ProtectedRoute><MainLayout><LazyInterestsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/creator" element={<ProtectedRoute><MainLayout><LazyCreatorDashboard /></MainLayout></ProtectedRoute>} />
      <Route path="/contact" element={<MainLayout><LazyContactPage /></MainLayout>} />
      <Route path="/payment/callback" element={<LazyPaymentCallbackPage />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ""}>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <AuthPromptProvider>
              <AppRouter />
              <Toaster position="top-center" richColors />
              <PushPromptBanner />
            </AuthPromptProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
